<script setup lang="ts">
import { BaseButton, BaseCheckbox } from '@/components/base'
import {
  FormDateTimePicker,
  FormDateWithTimePicker,
  FormInputField,
  FormMonthDays
} from '@/components/shared'
import { AssetType, ScheduleTimeBasedPattern, TimeFormatType } from '@/enum'
import { JobScheduleOptions } from '@/models'
import TimeBasedTrigger from '@/models/jobs/TimeBasedTrigger'
import { DAYS_OF_WEEK_TRIGGER_TYPES, TIME_BASED_TRIGGER_TYPES } from '@/utils/constants'
import CONSTANTS from '@/utils/constants/string-constants'
import {
  getCurrentTimeFormtted,
  greaterThanZeroRuleMachineHours,
  greaterThanZeroRuleOdometer,
} from '@/utils/helpers'
import { getRecurringLabel } from '@/utils/helpers/job-schedule-helpers'
import { computed, nextTick, ref, watch, watchPostEffect } from 'vue'

// UI vars
const TRIGGER_LABEL = 'Whichever occurs first will be triggered'
const odometerInputRef = ref()
const machineInputRef = ref()

// props
const props = defineProps<{
  startDateLabel?: string
  readonly?: boolean
  showOdometerTrigger?: boolean
  showMachineHoursTrigger?: boolean
  showTimeBasedTrigger?: boolean
  hideScheduledDate?: boolean
  hideExpectedCompletionDate?: boolean
  showTriggerLabel?: boolean
  showCheckboxes?: boolean
  jobSheduleText?: string
  isAssetBasedJob?: boolean
  selectedAssetType?: AssetType
}>()

// states
const jobStartDate = defineModel('jobStartDate', { default: '' })
const jobDueDate = defineModel('jobDueDate', { default: '' })
const scheduleOptions = defineModel('scheduleOptions', { default: new JobScheduleOptions() })
const isNeverEnding = defineModel('isNeverEnding', { default: false })
const selectedVehicle = defineModel<any>('selectedVehicle', { default: null })
const selectedEquipment = defineModel<any>('selectedEquipment', { default: null })
const selectedMachine = defineModel<any>('selectedMachine', { default: null })
const isOdometerBasedSelected = defineModel('isOdometerBasedSelected', { default: false })
const isMachineHoursBasedSelected = defineModel('isMachineHoursBasedSelected', { default: false })
const isTimeBasedSelected = defineModel('isTimeBasedSelected', { default: false })

// methods
const resetScheduledAndDueDates = () => {
  // jobStartDate.value = ''
  // jobDueDate.value = ''
}

const getRecurringLabelText = (
  repeatAfterEvery: number | null,
  pattern: ScheduleTimeBasedPattern
) => {
  return getRecurringLabel(scheduleOptions.value, repeatAfterEvery, pattern)
}

const resetMachineHoursReading = () => (scheduleOptions.value.machineHoursIncrementTrigger = null)
const resetTimeBasedTrigger = () =>
  (scheduleOptions.value.timeBasedTrigger = new TimeBasedTrigger())
const handleOdometerReadingChange = () =>
  (scheduleOptions.value.odometerReadingIncrementTrigger = null)

const handleRecurrencePatternChange = () => {
  scheduleOptions.value.timeBasedTrigger = {
    ...new TimeBasedTrigger(),
    pattern: scheduleOptions.value.timeBasedTrigger.pattern
  }
}

const handleScheduleAndDueDateChange = () => {
  if (!isTimeBasedSelected.value) resetScheduledAndDueDates()
  scheduleOptions.value.timeBasedTrigger = new TimeBasedTrigger()
}

const shouldDisableOdometerReading = computed(() => {
  const isVehicleCategorySelected = props.selectedAssetType === AssetType.VEHICLE
  const isVehicleSelected = selectedVehicle.value
  const isMachineCategorySelected = props.selectedAssetType === AssetType.MACHINE
  const isMachineSelected = selectedMachine.value
  return (
    props.isAssetBasedJob &&
    (!isVehicleCategorySelected || !isVehicleSelected) &&
    (!isMachineCategorySelected || !isMachineSelected)
  )
})

const shouldDisableMachineHoursReading = computed(() => {
  const isVehicleSelected = selectedVehicle.value
  const isEquipmentSelected = selectedEquipment.value
  const isMachineSelected = selectedMachine.value
  return props.isAssetBasedJob && !isVehicleSelected && !isEquipmentSelected && !isMachineSelected
})

const recurringJobMinimalStartDate = computed(() => {
  if (isNeverEnding.value || !jobStartDate.value) return
  return jobStartDate.value
})

watch(() => props.selectedAssetType, () => {
    isMachineHoursBasedSelected.value = false
    isOdometerBasedSelected.value = false

    nextTick(() => {
      scheduleOptions.value.machineHoursIncrementTrigger = null

      scheduleOptions.value.odometerReadingIncrementTrigger = null

      machineInputRef.value?.resetValidation?.()
      odometerInputRef.value?.resetValidation?.()
    })
})

watchPostEffect(() => {
  if (isNeverEnding.value) {
    scheduleOptions.value.endAt = null
    scheduleOptions.value.numberOfOccurrences = null
  }
})
</script>

<template>
  <v-divider class="my-2" v-if="!hideScheduledDate || !hideExpectedCompletionDate" dark />

  <!-- <span class="body-1 semibold">{{ CONSTANTS.JOB_SCHEDULE }}</span> -->

  <!--TRIGGER LABEL-->
  <div v-if="showTriggerLabel" class="schedule-notes px-2 py-1">
    <span class="schedule-notes-icon">
      <v-icon>mdi:mdi-information</v-icon>
    </span>
    <span>&nbsp;{{ TRIGGER_LABEL }}</span>
  </div>

  <div class="d-flex flex-column">
    <!--ODOMETER READING-->
    <v-row v-if="showOdometerTrigger" class="d-flex mb-4 mt-2">
      <v-col class="v-col flex-grow-0 pt-3">
        <BaseCheckbox
          v-model="isOdometerBasedSelected"
          :disabled="readonly || shouldDisableOdometerReading"
        />
      </v-col>
      <v-col
        class="v-col flex-grow-0 pt-3 pl-0 pr-6"
        :class="{ 'opacity-50': shouldDisableOdometerReading }"
      >
        <div class="repeat-every-label">
          <div>
            <span> Every </span>
          </div>
        </div>
      </v-col>
      <v-col class="flex-grow-0">
        <FormInputField
          ref="odometerInputRef"
          v-model.number="scheduleOptions.odometerReadingIncrementTrigger"
          class="repeat-every-width"
          :disabled="shouldDisableOdometerReading"
          label="Distance (miles or km)"
          hide-label
          type="number"
          :rules="[...greaterThanZeroRuleOdometer(props.selectedAssetType, isOdometerBasedSelected)]"
        />
      </v-col>
      <v-col class="flex-grow-0">
        <BaseButton
          @click="handleOdometerReadingChange"
          :disabled="shouldDisableOdometerReading"
          border
        >
          Reset
        </BaseButton>
      </v-col>
      <v-col class="text-left">
        <div
          v-if="
            selectedAssetType === AssetType.VEHICLE &&
            selectedVehicle?.id &&
            selectedVehicle?.odometerReading
          "
          class="pt-2"
        >
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1">
              {{ CONSTANTS.CURRENT_ODOMETER_READING }}: {{ selectedVehicle.odometerReading.value }}
              {{ selectedVehicle.odometerReading.unit }}
            </span>
          </div>
        </div>
        <div
          v-if="
            selectedAssetType === AssetType.MACHINE &&
            selectedMachine?.id &&
            selectedMachine?.odometerReading
          "
          class="pt-2"
        >
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1">
              {{ CONSTANTS.CURRENT_ODOMETER_READING }}: {{ selectedMachine.odometerReading.value }}
              {{ selectedMachine.odometerReading.unit }}
            </span>
          </div>
        </div>
      </v-col>
    </v-row>

    <!--MACHINE HOURS READING-->
    <v-row v-if="showMachineHoursTrigger" class="d-flex mb-8">
      <v-col class="v-col flex-grow-0 pt-3">
        <BaseCheckbox
          v-model="isMachineHoursBasedSelected"
          :disabled="readonly || shouldDisableMachineHoursReading"
        />
      </v-col>
      <v-col
        class="v-col flex-grow-0 pt-3 pl-0 pr-6"
        :class="{ 'opacity-50': shouldDisableOdometerReading }"
      >
        <div class="repeat-every-label">
          <div>
            <span> Every </span>
          </div>
        </div>
      </v-col>
      <v-col class="flex-grow-0">
        <FormInputField
          ref="machineHoursInputRef"
          v-model.number="scheduleOptions.machineHoursIncrementTrigger"
          class="repeat-every-width"
          :disabled="shouldDisableMachineHoursReading"
          label="Machine hours"
          type="number"
          :rules="[...greaterThanZeroRuleMachineHours(props.selectedAssetType, isMachineHoursBasedSelected)]"
        />
      </v-col>
      <v-col class="flex-grow-0">
        <BaseButton
          @click="resetMachineHoursReading"
          :disabled="shouldDisableMachineHoursReading"
          border
        >
          Reset
        </BaseButton>
      </v-col>
      <v-col class="text-left">
        <div v-if="selectedAssetType === AssetType.VEHICLE && selectedVehicle?.id" class="pt-2">
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1"
              >{{ CONSTANTS.CURRENT_MACHINE_HOURS_READING }}:
              {{ selectedVehicle.machineHours }}</span
            >
          </div>
        </div>
        <div v-if="selectedAssetType === AssetType.EQUIPMENT && selectedEquipment?.id" class="pt-2">
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1"
              >{{ CONSTANTS.CURRENT_MACHINE_HOURS_READING }}:
              {{ selectedEquipment.machineHours }}</span
            >
          </div>
        </div>
        <div v-if="selectedAssetType === AssetType.MACHINE && selectedMachine?.id" class="pt-2">
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1"
              >{{ CONSTANTS.CURRENT_MACHINE_HOURS_READING }}:
              {{ selectedMachine.machineHours }}</span
            >
          </div>
        </div>
      </v-col>
    </v-row>

    <!--JOB START AND DUE DATE-->
    <v-row no-gutters class="ga-6 flex-grow-0 pt-4">
      <v-col v-if="showCheckboxes" class="v-col flex-grow-0">
        <BaseCheckbox
          v-model="isTimeBasedSelected"
          @update:model-value="handleScheduleAndDueDateChange"
          :disabled="readonly"
        />
      </v-col>
      <v-col cols="4">
        <FormDateWithTimePicker
          :label="startDateLabel ?? 'Start from'"
          :disabled="readonly"
          start-of-day
          hide-time
          :time-format="TimeFormatType['12 hour']"
          v-model="jobStartDate"
          hide-details
        />
      </v-col>
      <v-col>
        <!-- <FormDateWithTimePicker
          label="Job due date"
          :disabled="readonly"
          end-of-day
          :start-from="jobStartDate"
          :time-format="TimeFormatType['12 hour']"
          v-model="jobDueDate"
          hide-details
        /> -->
      </v-col>
    </v-row>

    <!--REPEAT BY SELECTION-->
    <v-row
      v-if="jobStartDate && scheduleOptions.timeBasedTrigger.pattern == null"
      :class="`${showCheckboxes ? 'ml-9' : ''}`"
    >
      <v-col class="flex-grow-1 d-flex pl-3 text-red">
        Please select a repetition type from below
      </v-col>
    </v-row>

    <v-row :class="`${showCheckboxes ? 'ml-9' : ''}`">
      <v-col class="flex-grow-1 d-flex pl-3">
        <h4>Repeat every</h4>
      </v-col>
    </v-row>

    <!--RECURRENCE PATTERNS-->
    <v-row class="d-flex" :class="`${showCheckboxes ? 'ml-9' : ''}`">
      <v-col class="flex-grow-0 d-flex">
        <v-btn-toggle
          v-model="scheduleOptions.timeBasedTrigger.pattern"
          :multiple="false"
          :class="[!jobStartDate ? 'disabled-text' : '']"
        >
          <v-btn
            v-for="(triggerType, triggerIndex) in TIME_BASED_TRIGGER_TYPES"
            :value="triggerType.value"
            class="text-capitalize"
            @click="handleRecurrencePatternChange"
            :key="triggerIndex"
            style="height: 40px"
            :class="`schedule-pattern-button border base-button 
                  ${scheduleOptions.timeBasedTrigger.pattern == triggerType.value ? ' schedule-selected-days' : ''}`"
          >
            {{ triggerType.title }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col>
        <BaseButton
          class="ml-5"
          :class="[!jobStartDate ? 'disabled-text' : '']"
          border
          @click="resetTimeBasedTrigger"
        >
          Reset
        </BaseButton>
      </v-col>
      <v-col> </v-col>
    </v-row>

    <!--DAYS OF MONTH SELECTION-->
    <v-row
      v-if="scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.MONTHS"
      class="d-flex"
      :class="`${showCheckboxes ? 'ml-9' : ''}`"
    >
      <v-col class="flex-grow-1 pl-0 mt-0 pl-3">
        <FormMonthDays
          v-model="scheduleOptions.timeBasedTrigger.daysOfMonth"
          class="schedule-fields-min-width"
          v-bind="$attrs"
          :disabled="readonly"
        />
      </v-col>
      <v-col> </v-col>
    </v-row>

    <!--MULTIPLE DATES SELECTION-->
    <v-row
      v-if="scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.YEARS"
      class="d-flex"
      :class="`${showCheckboxes ? 'ml-9' : ''}`"
    >
      <v-col class="flex-grow-1 mt-8">
        <FormDateTimePicker
          v-model="scheduleOptions.timeBasedTrigger.dates"
          class="schedule-fields-min-width"
          v-bind="$attrs"
          :disabled="readonly"
          disable-past-dates
          disable-year-selection
          :next-dates-limit="365"
          multi-select
          no-range
          minimum-state
          no-actions
          small
          hide-details
          :label="'Select days of month'"
        />
      </v-col>
      <v-col> </v-col>
    </v-row>

    <!--WEEK DAYS SELECTION-->
    <v-row
      v-if="scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.WEEKS"
      class="d-flex"
      :class="`${showCheckboxes ? 'ml-9' : ''}`"
    >
      <v-col class="flex-grow-1 pl-3">
        <v-btn-toggle v-model="scheduleOptions.timeBasedTrigger.daysOfWeek" multiple>
          <v-btn
            v-for="(day, dayIndex) in DAYS_OF_WEEK_TRIGGER_TYPES"
            :value="day.value"
            class="text-capitalize"
            :key="dayIndex"
            :class="`schedule-days-button border base-button ${readonly ? 'disabled-btns' : ''} 
                  ${scheduleOptions.timeBasedTrigger.daysOfWeek.includes(day.value) ? ' schedule-selected-days' : ''}`"
          >
            {{ day.title }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col> </v-col>
    </v-row>

    <!--REPEAT AFTER EVERY SELECTION-->
    <v-row
      v-if="
        scheduleOptions.timeBasedTrigger &&
        (scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.DAYS ||
          scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.WEEKS ||
          scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.MONTHS ||
          scheduleOptions.timeBasedTrigger.pattern == ScheduleTimeBasedPattern.YEARS)
      "
      class="d-flex"
      :class="`${showCheckboxes ? 'ml-9' : ''}`"
    >
      <v-col class="flex-grow-1 pl-3">
        <FormInputField
          v-model="scheduleOptions.timeBasedTrigger.repeatAfterEvery"
          class="schedule-fields-min-width mt-6"
          type="number"
          :disabled="readonly"
          :label="
            getRecurringLabelText(
              scheduleOptions.timeBasedTrigger.repeatAfterEvery,
              scheduleOptions.timeBasedTrigger.pattern
            )
          "
          hide-details
        />
      </v-col>
      <v-col> </v-col>
    </v-row>
  </div>

  <v-divider class="mb-2 mt-2" style="max-width: 560px" dark />

  <!--TRIGGER LABEL-->
  <div class="schedule-notes px-2 py-1">
    <span class="schedule-notes-icon">
      <v-icon>mdi:mdi-information</v-icon>
    </span>
    <span>
      <span>&nbsp;{{ TRIGGER_LABEL }}</span>
    </span>
  </div>

  <div class="d-flex flex-column">
    <!--NEVER ENDS-->
    <v-row class="pb-4">
      <BaseCheckbox v-model="isNeverEnding" :disabled="readonly" class="pl-2 mt-2 pl-3"
        ><span class="pl-2"> Never ends </span>
      </BaseCheckbox>
    </v-row>

    <!--END DATE-->
    <v-row class="d-flex px-0 pl-3 pt-6">
      <v-col cols="4" class="flex-grow-1 d-flex pl-0 pr-0">
        <FormDateTimePicker
          v-model="scheduleOptions.endAt"
          :disabled="readonly || isNeverEnding"
          no-range
          no-actions
          small
          disable-past-dates
          :label="'End date'"
          :start-from="recurringJobMinimalStartDate"
          v-bind="$attrs"
        />
      </v-col>
    </v-row>

    <!--NUMBER OF RECURRENCE-->
    <v-row class="px-3 pr-0 d-flex pl-3">
      <v-col cols="4" class="flex-grow-1 pl-0 pr-0">
        <FormInputField
          type="number"
          v-model="scheduleOptions.numberOfOccurrences"
          :disabled="readonly || isNeverEnding"
          label="Total number of occurrences"
        />
      </v-col>
      <v-col> </v-col>
    </v-row>
  </div>

  <v-divider class="mb-2" dark />
</template>
